import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { tests, ideas, Idea, TestResult } from '../lib/api'

export default function ValidationPage() {
  const { id } = useParams<{ id: string }>()
  const [idea, setIdea] = useState<Idea | null>(null)
  const [results, setResults] = useState<TestResult[]>([])
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    ideas.get(id).then(setIdea).catch(() => {})
  }, [id])

  const handleRunTests = async () => {
    if (!id) return
    setRunning(true)
    setError(null)
    try {
      const data = await tests.run(id)
      setResults(data.tests || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to run tests')
    } finally {
      setRunning(false)
    }
  }

  const runnable = results.filter((t) => t.status === 'RUNNABLE').length
  const unrunnable = results.filter((t) => t.status === 'UNRUNNABLE').length
  const total = results.length

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6 max-w-5xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link
          to={id ? `/ideas/${id}` : '/dashboard'}
          className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-gray-800 rounded transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-100">Validation Dashboard</h1>
          {idea && (
            <p className="text-sm text-gray-400">{idea.title}</p>
          )}
        </div>
        <button
          onClick={handleRunTests}
          disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50"
        >
          {running ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Running...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Run Tests
            </>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-950 border border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Results summary */}
      {results.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-gray-100">{total}</p>
            <p className="text-xs text-gray-400">Total Tests</p>
          </div>
          <div className="bg-gray-900 border border-green-900/30 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-400">{runnable}</p>
            <p className="text-xs text-gray-400">Runnable</p>
          </div>
          <div className="bg-gray-900 border border-red-900/30 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-red-400">{unrunnable}</p>
            <p className="text-xs text-gray-400">Unrunnable</p>
          </div>
        </div>
      )}

      {/* Test results */}
      {results.length > 0 ? (
        <div className="space-y-2">
          {results.map((test, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg border ${
                test.status === 'RUNNABLE'
                  ? 'bg-green-950/30 border-green-900/30'
                  : 'bg-red-950/30 border-red-900/30'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {test.status === 'RUNNABLE' ? (
                    <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <span className="text-sm font-medium text-gray-200">
                    {test.test_name || `Test ${i + 1}`}
                  </span>
                </div>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                    test.status === 'RUNNABLE'
                      ? 'bg-green-900/40 text-green-300'
                      : 'bg-red-900/40 text-red-300'
                  }`}
                >
                  {test.status}
                </span>
              </div>
              {test.reason && (
                <p className="text-xs text-gray-400 mt-1">{test.reason}</p>
              )}
            </div>
          ))}
        </div>
      ) : !running ? (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-700 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-300 mb-1">
            No test results yet
          </h3>
          <p className="text-gray-500 text-sm">
            Click "Run Tests" to validate your idea against the test harness.
          </p>
        </div>
      ) : null}
    </div>
  )
}
